import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';

export interface ConfirmDialogCheckbox {
  label: string;
  defaultChecked?: boolean;
}

export interface ConfirmDialogConfirmation {
  confirmed: true;
  checkboxChecked?: boolean;
}

export type ConfirmDialogResult = boolean | 'secondary' | ConfirmDialogConfirmation;

/**
 * Data passed to the confirmation dialog when opened.
 */
export interface ConfirmDialogData {
  /** Title of the dialog */
  title: string;
  /** Message to display */
  message: string;
  /** Text for the confirm button (default: 'common.confirm') */
  confirmText?: string;
  /** Text for the cancel button (default: 'common.cancel') */
  cancelText?: string;
  /** Whether the action is destructive (styles the confirm button as danger) */
  destructive?: boolean;
  /** Optional informational text shown before actions */
  infoText?: string;
  /** Optional checkbox shown before actions */
  checkbox?: ConfirmDialogCheckbox;
  /** Optional secondary action shown between cancel and confirm */
  secondaryText?: string;
  /**
   * When `true` (and `checkbox` is set), the confirm button stays disabled
   * until the checkbox is checked — for confirmations that must not be
   * dismissable by reflex (e.g. the F20 past-impact gate: "Ich habe die
   * Folgen geprüft").
   */
  requireCheckedToConfirm?: boolean;
}

/**
 * A reusable confirmation dialog component.
 *
 * @example
 * ```typescript
 * const dialogRef = this.dialog.open(ConfirmDialogComponent, {
 *   data: {
 *     title: 'Delete Plan',
 *     message: 'Are you sure you want to delete this plan?',
 *     confirmText: 'Delete',
 *     destructive: true,
 *   },
 * });
 *
 * dialogRef.closed.subscribe((confirmed) => {
 *   if (confirmed) {
 *     // User confirmed, proceed with deletion
 *   }
 * });
 * ```
 */
@Component({
  selector: 'zag-confirm-dialog',
  imports: [TranslatePipe, ZagButton, ZagIcon],
  template: `
    <div class="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="zag-confirm-dialog-title">
      <header class="confirm-dialog__header">
        <h2 id="zag-confirm-dialog-title" class="confirm-dialog__title">{{ data.title | translate }}</h2>
        <button
          type="button"
          zagButton="basic"
          zagType="secondary"
          zagSize="sm"
          class="confirm-dialog__close"
          [attr.aria-label]="'common.close' | translate"
          (click)="onCancel()"
        >
          <span zagIcon="close" aria-hidden="true"></span>
        </button>
      </header>
      <p class="confirm-dialog__message">{{ data.message | translate }}</p>
      @if (data.infoText) {
        <p class="confirm-dialog__info confirm-dialog__info--multiline">{{ data.infoText | translate }}</p>
      }

      @if (data.checkbox; as checkbox) {
        <label class="confirm-dialog__checkbox">
          <input type="checkbox" [checked]="checkboxChecked()" (change)="onCheckboxChange($event)" />
          <span>{{ checkbox.label | translate }}</span>
        </label>
      }

      <div class="confirm-dialog__actions">
        <button
          type="button"
          zagButton="outlined"
          zagType="secondary"
          class="confirm-dialog__btn confirm-dialog__btn--cancel"
          (click)="onCancel()"
        >
          {{ data.cancelText ?? 'common.cancel' | translate }}
        </button>
        @if (data.secondaryText) {
          <button type="button" zagButton="outlined" zagType="secondary" class="confirm-dialog__btn" (click)="onSecondary()">
            {{ data.secondaryText | translate }}
          </button>
        }
        <button
          type="button"
          zagButton
          [zagType]="data.destructive ? 'error' : 'primary'"
          class="confirm-dialog__btn"
          [class.confirm-dialog__btn--danger]="data.destructive"
          [class.confirm-dialog__btn--primary]="!data.destructive"
          [disabled]="confirmDisabled()"
          (click)="onConfirm()"
        >
          {{ data.confirmText ?? 'common.confirm' | translate }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .confirm-dialog {
        max-width: var(--dialog-width-min-wide);
        background: var(--background-color-elevated);
        border-radius: var(--border-radius-lg);
        padding: var(--spacing-lg);
        box-shadow: var(--shadow-lg);
      }

      .confirm-dialog__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--spacing-md);
        margin-bottom: var(--spacing-md);
      }

      .confirm-dialog__title {
        margin: 0;
        font-size: var(--font-size-lg);
        font-weight: var(--font-weight-semibold);
        color: var(--text-color-primary);
      }

      .confirm-dialog__close.zag-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: var(--spacing-xl);
        height: var(--spacing-xl);
        padding: 0;
        border: 0;
        border-radius: var(--border-radius-sm);
        background: transparent;
        color: var(--text-color-secondary);
        cursor: pointer;
      }

      .confirm-dialog__close.zag-button:hover:not(:disabled):not(.zag-button--loading):not(.zag-button--disabled) {
        background: var(--surface-color-hover);
        color: var(--text-color-primary);
      }

      .confirm-dialog__close.zag-button:focus-visible:not(:disabled):not(.zag-button--loading):not(.zag-button--disabled) {
        outline: var(--border-width-thick) solid var(--color-primary);
        outline-offset: var(--spacing-2xs);
      }

      .confirm-dialog__message {
        margin: 0 0 var(--spacing-lg);
        font-size: var(--font-size-md);
        color: var(--text-color-secondary);
        line-height: 1.5;
      }

      .confirm-dialog__info {
        margin: 0 0 var(--spacing-lg);
        padding: var(--spacing-sm) var(--spacing-md);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-md);
        background: var(--surface-color-elevated, var(--background-color-elevated));
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        line-height: 1.5;
      }

      .confirm-dialog__info--multiline {
        white-space: pre-line;
      }

      .confirm-dialog__checkbox {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin: 0 0 var(--spacing-lg);
        color: var(--text-color-primary);
        cursor: pointer;
      }

      .confirm-dialog__checkbox input {
        width: var(--spacing-md);
        height: var(--spacing-md);
        margin: 0;
        accent-color: var(--primary-color);
      }

      .confirm-dialog__actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-sm);
        justify-content: flex-end;
      }

      .confirm-dialog__btn {
        min-width: var(--spacing-4xl);
      }

      .confirm-dialog__btn--cancel {
        --button-border-color: var(--border-color);
      }

      .confirm-dialog__btn--primary {
        --button-bg: var(--primary-color);
        --button-color: var(--primary-color-contrast);
      }

      .confirm-dialog__btn--danger {
        --button-bg: var(--danger-color);
        --button-color: var(--danger-color-contrast, #fff);
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscapeKey()',
  },
})
export class ConfirmDialogComponent {
  private readonly dialogRef = inject(DialogRef<ConfirmDialogResult>);
  readonly data = inject<ConfirmDialogData>(DIALOG_DATA);
  protected readonly checkboxChecked = signal(this.data.checkbox?.defaultChecked ?? false);

  protected readonly confirmDisabled = computed(
    () => !!this.data.checkbox && !!this.data.requireCheckedToConfirm && !this.checkboxChecked()
  );

  protected onEscapeKey(): void {
    this.onCancel();
  }

  protected onConfirm(): void {
    if (this.confirmDisabled()) return;

    if (!this.data.checkbox) {
      this.dialogRef.close(true);
      return;
    }

    this.dialogRef.close({
      confirmed: true,
      checkboxChecked: this.checkboxChecked(),
    });
  }

  protected onSecondary(): void {
    this.dialogRef.close('secondary');
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  protected onCheckboxChange(event: Event): void {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    this.checkboxChecked.set(target.checked);
  }
}
